// THE PLATFORM CONSOLE'S ROUTE TABLE (spec 15 §4, admin.<apex> only; D-626).
//
// ============================== THE CONTRACT ==============================
// Four M15 lanes add screens to this console at once. Each adds a screen with
// APPEND-ONLY edits in three places, and changes nothing else in this file:
//
//   1. A ROUTE here: one createRoute call bound to a `const`, with
//      `getParentRoute: () => consoleRoute` and a RELATIVE `path` ("staff",
//      "audit-log", "health/$tenantId"), so it renders inside the console
//      shell, behind the confirmed-session guard. Read the signed-in staff
//      member with `useConsoleMe()`; never probe `/me` again. Then append the
//      route to the `consoleRoute.addChildren([...])` list at the bottom,
//      above the marker line. One `createRoute` per line of that list.
//   2. A NAV ENTRY in console/console-nav.ts: append an entry to CONSOLE_NAV
//      under its job group (fleet, tenants, support, health, usage, staff,
//      account). The `visible` predicate mirrors the route's server-side
//      permission (spec 15 §7); the server stays the fence. A group with no
//      visible entry is not drawn, so nothing is shown for a screen that does
//      not exist yet.
//   3. THE SWEEP. The locale-viewport sweep must account for the new path
//      (tools/ci/route-sweep-coverage.ts parses this file as well as
//      router.tsx): add a skip-with-reason entry to e2e/specs/sweeps/
//      W-SWEEP.locale-viewport.spec.ts, raise its ROUTE_LEDGER_COUNT with the
//      path named in the pin comment, and add the path to the gate's
//      DECLARED_ELSEWHERE map with its reason.
//
// Every screen opens with the design system's `PageHeader` (crumbs on a
// detail page: the list it came from, then the record), and renders loading,
// error and empty states with components/states. The shell draws the frame,
// the nav, the environment badge, the signed-in staff member and sign-out; a
// screen draws none of those.
// ==========================================================================
//
// One bundle serves every host, so these routes exist everywhere and a tenant
// host refuses them in RootLayout's `isAdminOnlyPath` branch with the designed
// 404. `/login`, `/forgot-password`, `/reset` and `/` are shared paths decided
// by host in router.tsx and routes/*.
//
// `/accept-invite` needs no session: the invite token is the credential.
// `/enroll` sits behind the guard in its `pending` phase. Everything under
// `/console` sits behind the guard in its `confirmed` phase, ONCE, in the
// layout route below, so moving between console screens keeps the shell
// mounted and never shows the session check again.

import { createRoute, Outlet } from "@tanstack/react-router";
import { rootRoute } from "../../routes/root-route";
import { AdminShell } from "./admin-shell";
import { RequirePlatformSession } from "./components/require-session";
import { ConsoleMeProvider, useConsoleMe } from "./console/console-me";
import { ConsoleNotFound, ConsoleShell } from "./console/console-shell";
import { AcceptInviteScreen } from "./screens/accept-invite";
import { AdminAccountScreen } from "./screens/account";
import { AdminEnrollScreen } from "./screens/enroll";
import { FleetOverviewScreen } from "./screens/fleet-overview";
import { TenantCreateScreen } from "./screens/tenant-create";
import { TenantDetailScreen } from "./screens/tenant-detail";
import { TenantListScreen } from "./screens/tenant-list";
import { TENANT_STATUSES } from "./lib/tenants";
// M15-4 (D-611 to D-613): the health board, a tenant's health and the logs.
import { HealthBoardScreen } from "./screens/health-board";
import { LogsScreen } from "./screens/logs";
import { TenantHealthScreen } from "./screens/tenant-health";
// M15-6 and M15-7a: plans, a tenant's entitlements and its usage.
import { PlansScreen } from "./screens/plans";
import { TenantEntitlementsScreen } from "./screens/tenant-entitlements";
import { TenantUsageScreen } from "./screens/tenant-usage";
// M15-8 and M15-9: the support tab, staff management and the audit log.
import { AuditLogScreen } from "./screens/audit-log";
import { StaffListScreen } from "./screens/staff-list";
import { TenantSupportScreen } from "./screens/tenant-support";
import { LowRecoveryCodesNotice } from "./components/console-frame";
// D-645: S23, a tenant's agents and their model profiles.
import { TenantAgentsScreen } from "./screens/tenant-agents";

const acceptInviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/accept-invite",
  component: AcceptInviteScreen,
});

const adminEnrollRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/enroll",
  component: () => (
    <RequirePlatformSession requires="pending">
      {() => <AdminEnrollScreen />}
    </RequirePlatformSession>
  ),
});

/** The signed-in console: the guard once, then the shell around the screen.
 * The guard's own checking and error states keep the sign-in frame, so the
 * shell never paints for someone it then turns away. */
function ConsoleLayout() {
  return (
    <RequirePlatformSession requires="confirmed" frame={(node) => <AdminShell>{node}</AdminShell>}>
      {(me) => (
        <ConsoleMeProvider me={me}>
          <ConsoleShell me={me}>
            {/* §3.1 step 4 (M15-9): asks for new recovery codes on every
                screen while three or fewer are left. */}
            <LowRecoveryCodesNotice me={me} />
            <Outlet />
          </ConsoleShell>
        </ConsoleMeProvider>
      )}
    </RequirePlatformSession>
  );
}

const consoleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/console",
  component: ConsoleLayout,
  // A console path no screen answers stays inside the console, with the way
  // back, rather than falling through to the office's 404.
  notFoundComponent: ConsoleNotFound,
});

// ---- S3, the fleet overview: the console's home (D-625) -------------------
const consoleHomeRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/",
  component: function ConsoleHomeRouteScreen() {
    return <FleetOverviewScreen me={useConsoleMe()} />;
  },
});

// ---- S2a, the signed-in staff member's own account ------------------------
const consoleAccountRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "account",
  component: function ConsoleAccountRouteScreen() {
    return <AdminAccountScreen me={useConsoleMe()} />;
  },
});

// ---- S4, S11, S5: the tenants ---------------------------------------------
/** `?status=` opens the list filtered (the fleet tiles link here). Anything
 * that is not a tenant status is dropped rather than sent to the API. */
interface TenantListSearch {
  status?: string;
}

const consoleTenantsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "tenants",
  validateSearch: (search: Record<string, unknown>): TenantListSearch => {
    const status = search["status"];
    return typeof status === "string" && (TENANT_STATUSES as readonly string[]).includes(status)
      ? { status }
      : {};
  },
  component: function ConsoleTenantsRouteScreen() {
    const { status } = consoleTenantsRoute.useSearch();
    return (
      <TenantListScreen
        key={status ?? ""}
        me={useConsoleMe()}
        {...(status !== undefined ? { initialStatus: status } : {})}
      />
    );
  },
});

const consoleTenantCreateRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "tenants/new",
  component: function ConsoleTenantCreateRouteScreen() {
    return <TenantCreateScreen me={useConsoleMe()} />;
  },
});

const consoleTenantDetailRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "tenants/$tenantId",
  component: function ConsoleTenantDetailRouteScreen() {
    const { tenantId } = consoleTenantDetailRoute.useParams();
    return <TenantDetailScreen me={useConsoleMe()} tenantId={tenantId} />;
  },
});

// ---- S15, S9 and the logs view (M15-4, D-611 to D-613) --------------------
// Every staff role reads them; `?tenant=` narrows the logs to one tenant and
// anything that is not a uuid is dropped rather than sent to the API.
const consoleHealthRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "health",
  component: function ConsoleHealthRouteScreen() {
    return <HealthBoardScreen />;
  },
});

const consoleTenantHealthRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "tenants/$tenantId/health",
  component: function ConsoleTenantHealthRouteScreen() {
    const { tenantId } = consoleTenantHealthRoute.useParams();
    return <TenantHealthScreen me={useConsoleMe()} tenantId={tenantId} />;
  },
});

const UUID_SEARCH = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;

const consoleLogsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "logs",
  validateSearch: (search: Record<string, unknown>): { tenant?: string } =>
    typeof search["tenant"] === "string" && UUID_SEARCH.test(search["tenant"])
      ? { tenant: search["tenant"].toLowerCase() }
      : {},
  component: function ConsoleLogsRouteScreen() {
    const { tenant } = consoleLogsRoute.useSearch();
    return <LogsScreen tenantId={tenant} />;
  },
});

// ---- S12 plans, S6 entitlements and S7 usage (M15-6, M15-7a) -------------
const consolePlansRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "plans",
  component: function ConsolePlansRouteScreen() {
    return <PlansScreen me={useConsoleMe()} />;
  },
});

const consoleTenantEntitlementsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "tenants/$tenantId/entitlements",
  component: function ConsoleTenantEntitlementsRouteScreen() {
    const { tenantId } = consoleTenantEntitlementsRoute.useParams();
    return <TenantEntitlementsScreen me={useConsoleMe()} tenantId={tenantId} />;
  },
});

const consoleTenantUsageRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "tenants/$tenantId/usage",
  component: function ConsoleTenantUsageRouteScreen() {
    const { tenantId } = consoleTenantUsageRoute.useParams();
    return <TenantUsageScreen me={useConsoleMe()} tenantId={tenantId} />;
  },
});

// ---- S10 support, S17 staff and S18 the audit log (M15-8, M15-9) ---------
const consoleTenantSupportRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "tenants/$tenantId/support",
  component: function ConsoleTenantSupportRouteScreen() {
    const { tenantId } = consoleTenantSupportRoute.useParams();
    return <TenantSupportScreen me={useConsoleMe()} tenantId={tenantId} />;
  },
});

const consoleStaffRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "staff",
  component: function ConsoleStaffRouteScreen() {
    return <StaffListScreen me={useConsoleMe()} />;
  },
});

/** `?tenant=` opens the audit log filtered to the tenant a tenant's page
 * linked from; anything that is not a uuid is dropped. */
const consoleAuditRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "audit",
  validateSearch: (search: Record<string, unknown>): { tenant?: string } =>
    typeof search["tenant"] === "string" && UUID_SEARCH.test(search["tenant"])
      ? { tenant: search["tenant"].toLowerCase() }
      : {},
  component: function ConsoleAuditRouteScreen() {
    const { tenant } = consoleAuditRoute.useSearch();
    return (
      <AuditLogScreen me={useConsoleMe()} {...(tenant !== undefined ? { tenantId: tenant } : {})} />
    );
  },
});

// ---- S23 a tenant's agents and their model profiles (D-645) --------------
const consoleTenantAgentsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "tenants/$tenantId/agents",
  component: function ConsoleTenantAgentsRouteScreen() {
    const { tenantId } = consoleTenantAgentsRoute.useParams();
    return <TenantAgentsScreen me={useConsoleMe()} tenantId={tenantId} />;
  },
});

const consoleRouteTree = consoleRoute.addChildren([
  consoleHomeRoute,
  consoleAccountRoute,
  consoleTenantsRoute,
  consoleTenantCreateRoute,
  consoleTenantDetailRoute,
  consoleHealthRoute,
  consoleTenantHealthRoute,
  consoleLogsRoute,
  consolePlansRoute,
  consoleTenantEntitlementsRoute,
  consoleTenantUsageRoute,
  consoleTenantSupportRoute,
  consoleStaffRoute,
  consoleAuditRoute,
  consoleTenantAgentsRoute,
  // ---- other lanes append their console routes above this line ----
]);

/** Spread into router.tsx's root children. */
export const consoleRouteTrees = [acceptInviteRoute, adminEnrollRoute, consoleRouteTree] as const;
